import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext';
// @ts-ignore
import { Helmet } from 'react-helmet';

const AsciiArtGenerator: React.FC = () => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [asciiArt, setAsciiArt] = useState<string>('');
  const [rawAsciiArt, setRawAsciiArt] = useState<string>('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedCharSet, setSelectedCharSet] = useState<'dots' | 'blocks' | 'simple' | 'complex'>('blocks');
  const [brushSize, setBrushSize] = useState(5);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastImageData = useRef<ImageData | null>(null);

  // Different character sets for different styles
  const charSets = {
    dots: '⠀⠁⠂⠃⠄⠅⠆⠇⡀⡁⡂⡃⡄⡅⡆⡇⢀⢁⢂⢃⢄⢅⢆⢇⣀⣁⣂⣃⣄⣅⣆⣇', // Braille patterns for better detail
    blocks: '░▒▓█', // Keep blocks as is
    simple: ' .:-=+*#%@', // Keep simple as is
    complex: ' .\'`^",:;Il!i><~+_-?][}{1)(|/\\tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$', // Keep complex as is
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Restore canvas size to original
    canvas.width = 500;
    canvas.height = 500;

    // Set initial canvas style
    ctx.fillStyle = isDarkMode ? '#000000' : '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [isDarkMode]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = isDarkMode ? '#ffffff' : '#000000';
    ctx.lineWidth = brushSize;
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
    convertToAscii();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const convertToAscii = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    lastImageData.current = imageData;

    const { data } = imageData;
    // Use consistent ratio for both width and height
    // Most monospace fonts have a 2:1 aspect ratio (width:height)
    const charWidth = 6;  // Width of a character in pixels
    const charHeight = 12; // Height of a character in pixels
    const asciiWidth = Math.floor(canvas.width / charWidth);
    const asciiHeight = Math.floor(canvas.height / charHeight);
    let rawResult = '';
    let displayResult = '';

    const chars = charSets[selectedCharSet];

    for (let y = 0; y < asciiHeight; y++) {
      for (let x = 0; x < asciiWidth; x++) {
        let totalR = 0, totalG = 0, totalB = 0;
        const sampleSize = 2;
        let sampleCount = 0;

        for (let dy = 0; dy < sampleSize; dy++) {
          for (let dx = 0; dx < sampleSize; dx++) {
            const pixelY = y * charHeight + dy * (charHeight / sampleSize);
            const pixelX = x * charWidth + dx * (charWidth / sampleSize);
            if (pixelY < canvas.height && pixelX < canvas.width) {
              const pixelIndex = (Math.floor(pixelY) * canvas.width + Math.floor(pixelX)) * 4;
              totalR += data[pixelIndex];
              totalG += data[pixelIndex + 1];
              totalB += data[pixelIndex + 2];
              sampleCount++;
            }
          }
        }

        const r = Math.floor(totalR / sampleCount);
        const g = Math.floor(totalG / sampleCount);
        const b = Math.floor(totalB / sampleCount);
        
        const brightness = (r + g + b) / 3;
        const adjustedBrightness = isDarkMode ? brightness : 255 - brightness;
        const charIndex = Math.floor((adjustedBrightness / 255) * (chars.length - 1));
        const char = chars[charIndex];

        rawResult += char;
        displayResult += `<span style="color: ${isDarkMode ? '#ffffff' : '#000000'}">${char}</span>`;
      }
      rawResult += '\n';
      displayResult += '\n';
    }

    // Store both raw and display versions
    setRawAsciiArt(rawResult);
    setAsciiArt(displayResult);
  };

  useEffect(() => {
    if (lastImageData.current) {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.putImageData(lastImageData.current, 0, 0);
      convertToAscii();
    }
  }, [selectedCharSet, isDarkMode]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = isDarkMode ? '#000000' : '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setAsciiArt('');
    setRawAsciiArt('');
  };

  const cleanAsciiArt = (text: string): string => {
    // Create a temporary div to properly parse HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    
    // Get all text content and split into lines
    const lines = text
      ?.split('\n')
      .map(line => {
        // Remove HTML tags
        return line.replace(/<[^>]*>/g, '');
      })
      .filter(line => {
        // Check if line contains any non-blank characters
        // For Braille patterns, we consider the blank pattern (⠀) as empty
        const hasNonBlank = Array.from(line).some(char => {
          // Check if it's a Braille pattern blank
          if (char.charCodeAt(0) === 10240) return false;
          // For other characters, check if they're not whitespace
          return !/\s/.test(char);
        });
        return hasNonBlank;
      }) || [];

    // If no lines found, return empty string
    if (lines.length === 0) return '';

    // Return the original ASCII art without any character set conversion
    return lines.join('\n').trim();
  };

  const handleDownload = () => {
    const cleanedContent = cleanAsciiArt(rawAsciiArt);
    const blob = new Blob([cleanedContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ascii-art.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    const cleanedContent = cleanAsciiArt(rawAsciiArt);
    console.log(cleanedContent);
    navigator.clipboard.writeText(cleanedContent);
  };

  return (
    <>
      <Helmet>
        <title>ASCII Art Generator | Convert Images to ASCII - Davinqi</title>
        <meta name="description" content="Convert images to ASCII art online with Davinqi ASCII Art Generator. Fun, fast, and privacy-friendly." />
        <meta property="og:title" content="ASCII Art Generator | Convert Images to ASCII - Davinqi" />
        <meta property="og:description" content="Convert images to ASCII art online with Davinqi ASCII Art Generator. Fun, fast, and privacy-friendly." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi.com/art/ascii" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ASCII Art Generator | Convert Images to ASCII - Davinqi" />
        <meta name="twitter:description" content="Convert images to ASCII art online with Davinqi ASCII Art Generator. Fun, fast, and privacy-friendly." />
      </Helmet>
      <div className="absolute inset-0 flex flex-col bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="flex-none p-2 text-center relative">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            ASCII Art Generator
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Draw and convert to ASCII art in real-time
          </p>
          <div className="absolute top-2 right-2">
            <button
              onClick={toggleDarkMode}
              className="p-1.5 rounded-lg bg-gray-200 dark:bg-gray-700 
                       hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              {isDarkMode ? (
                <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex gap-4 p-4 pl-20 overflow-hidden">
          {/* Left Panel - Drawing Canvas */}
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 flex flex-col min-h-0">
            <div className="flex-1 relative flex items-center justify-center">
              <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                className="border border-gray-300 dark:border-gray-600 rounded-lg cursor-crosshair"
                style={{ width: '500px', height: '500px' }}
              />
            </div>
            <div className="mt-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Brush:</label>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={brushSize}
                    onChange={(e) => setBrushSize(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{brushSize}px</span>
                </div>
                <select
                  value={selectedCharSet}
                  onChange={(e) => setSelectedCharSet(e.target.value as any)}
                  className="px-3 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="dots">Dots</option>
                  <option value="blocks">Blocks</option>
                  <option value="simple">Simple</option>
                  <option value="complex">Complex</option>
                </select>
                <button
                  onClick={clearCanvas}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 
                           transition-colors duration-200"
                >
                  Clear Canvas
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - ASCII Art Display */}
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 flex flex-col min-h-0">
            <div className="flex-1 relative overflow-auto flex items-center justify-center">
              {asciiArt ? (
                <pre 
                  className="whitespace-pre font-mono text-[10px]"
                  style={{ 
                    lineHeight: '1.2',
                    letterSpacing: '0.5px',
                    backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                    color: isDarkMode ? '#ffffff' : '#000000',
                    fontFamily: 'monospace'
                  }}
                  dangerouslySetInnerHTML={{ __html: asciiArt }}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500 dark:text-gray-400">
                  Start drawing to see the ASCII art conversion
                </div>
              )}
            </div>
            {asciiArt && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={handleCopy}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 
                           transition-colors duration-200"
                >
                  Copy to Clipboard
                </button>
                <button
                  onClick={handleDownload}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 
                           transition-colors duration-200"
                >
                  Download
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AsciiArtGenerator; 